import {useCallback, useEffect, useRef, useState} from 'react';

import {listEnrichmentProducts, listVariationGroups} from "api/enrichment";
import {mapMerchandisingProductToProduct} from "utils/mapMerchandisingProduct";

import {ENRICHMENT_STATE_FIELDS, ENRICHMENT_TABS, isAttributeTab} from "../enrichmentConstants";

function toEnrichmentProduct(document) {
    return {
        ...mapMerchandisingProductToProduct(document),
        ...Object.fromEntries(Object.values(ENRICHMENT_STATE_FIELDS)
            .flatMap((fields) => Object.values(fields))
            .map((field) => [field, document[field] ?? []])),
        enrichmentSuggestions: document.enrichmentSuggestions ?? [],
        retailerName: document.retailer ?? null,
        potentialScore: document.potentialScore ?? null,
    };
}

export default function useEnrichmentList(retailerId, body, {tab, state, target, search, page, itemsPerPage, sort}) {
    const [rows, setRows] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState({});
    const editedValues = useRef({});

    useEffect(() => {
        setSelected({});
        editedValues.current = {};

        const isImages = ENRICHMENT_TABS.ON_MODEL_IMAGES === tab;
        if (!isAttributeTab(tab) && !(isImages && 'missing' === state)) {
            setRows([]);
            setTotalItems(0);
            setLoading(false);

            return;
        }

        setLoading(true);
        setRows([]);
        let request;
        if (isImages) {
            const query = new URLSearchParams({groupBy: 'pictures', firstPictureWorn: 'false', page: String(page), itemsPerPage: String(itemsPerPage)});
            if ('' !== search) {
                query.set('search', search);
            }
            request = listVariationGroups(retailerId, query);
        } else {
            request = listEnrichmentProducts(retailerId, {
                ...body,
                page,
                itemsPerPage,
                sort: {[sort.property]: {order: sort.order}},
                enrichmentTab: tab,
                enrichmentState: state,
                enrichmentTargets: null !== target ? [target] : [],
            });
        }

        let current = true;
        request.then((response) => {
            if (!current) {
                return;
            }
            const members = response['hydra:member'] ?? [];
            setRows(isImages ? members : members.map(toEnrichmentProduct));
            setTotalItems(response['hydra:totalItems'] ?? 0);
        }).catch(() => {
            if (current) {
                setRows([]);
                setTotalItems(0);
            }
        }).finally(() => {
            if (current) {
                setLoading(false);
            }
        });

        return () => {
            current = false;
        };
    }, [tab, state, target, page, itemsPerPage, sort, retailerId, body, search]);

    const resetList = useCallback(() => {
        setRows([]);
        setTotalItems(0);
        setLoading(true);
        setSelected({});
    }, []);

    const handleValuesChange = useCallback((productId, values) => {
        editedValues.current[productId] = values;
    }, []);

    return {rows, setRows, totalItems, loading, selected, setSelected, editedValues, handleValuesChange, resetList};
}
